import apiClient from '@/shared/api/client'
import type { User, AssignRolesRequest } from '@/shared/types/auth'

interface ListParams {
  page?: number
  page_size?: number
  search?: string
  is_active?: boolean
}

interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

interface CreateUserRequest {
  email: string
  username: string
  full_name: string
  password: string
  employee_code?: string
}

interface UpdateUserRequest {
  email?: string
  full_name?: string
  employee_code?: string
}

export interface RoleOption {
  id: string
  role_code: string
  role_name: string
}

export const usersApi = {
  list: async (params?: ListParams): Promise<PaginatedResponse<User>> => {
    const res = await apiClient.get<PaginatedResponse<User>>('/users', { params })
    return res.data
  },

  get: async (id: string): Promise<User> => {
    const res = await apiClient.get<User>(`/users/${id}`)
    return res.data
  },

  create: async (data: CreateUserRequest): Promise<User> => {
    const res = await apiClient.post<User>('/users', data)
    return res.data
  },

  update: async (id: string, data: UpdateUserRequest): Promise<User> => {
    const res = await apiClient.patch<User>(`/users/${id}`, data)
    return res.data
  },

  activate: async (id: string): Promise<User> => {
    const res = await apiClient.post<User>(`/users/${id}/activate`)
    return res.data
  },

  deactivate: async (id: string): Promise<User> => {
    const res = await apiClient.post<User>(`/users/${id}/deactivate`)
    return res.data
  },

  // GOV-001 P5 — returns a PENDING_APPROVAL envelope (grant applies
  // after ERP Coordinator approval), or the User when ?emergency=true.
  // Emergency requires SYSTEM_ADMIN and is audit-logged separately
  // as EMERGENCY_DIRECT_GRANT.
  assignRoles: async (
    id: string, data: AssignRolesRequest, emergency = false,
  ): Promise<User | { approval_status: string; message?: string }> => {
    const res = await apiClient.put(`/users/${id}/roles`, data, {
      params: emergency ? { emergency: true } : undefined,
    })
    return res.data
  },

  resetPassword: async (id: string, password: string): Promise<void> => {
    await apiClient.patch(`/users/${id}`, { password })
  },

  // Sprint 0148 — soft delete. Reversible only by DB (intentional).
  // Pre-condition: user.delete permission. You cannot delete yourself.
  delete: async (id: string, reason?: string): Promise<User> => {
    const res = await apiClient.delete<User>(`/users/${id}`, {
      data: { reason: reason ?? null },
    })
    return res.data
  },
}

export const rolesApi = {
  list: async (): Promise<RoleOption[]> => {
    const res = await apiClient.get('/roles', { params: { size: 100 } })
    return res.data?.items ?? res.data ?? []
  },
}
